import { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, type Control, type FieldValues } from 'react-hook-form';
import { useGateValue } from '@statsig/react-bindings';
import { FEATURE_FLAGS } from '@/shared/types/features';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Checkbox,
  Flex,
  Icon,
  Tabs,
  TabsContent,
  TabsTrigger,
  TabsList,
  Text,
  cn,
  Form,
  FormField,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
  toast,
  Separator,
} from '@kognitos/lattice';
import { captureException } from '@/modules/sentry';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores/user-preferences.store';
import {
  Bdkv2OAuthFlow,
  V2BookConnectionState,
  type V2BookConnectionDescriptor,
  type Bdkv2BookDescriptor,
  type V2BookConnectionResponse,
  type Bdkv2Value,
} from '@/generated/hooks/bdk/models';
import {
  ConfigMapStep,
  CustomCredentialsStep,
  OAuthClientCredentialsStep,
  ReuseTestConnectionAlert,
  useReuseTestConnectionAlert,
} from '..';
import {
  EnvironmentType,
  createAuthenticationCredentialsSchema,
  createConfigMapSchema,
  createConnectionNameValidation,
} from '../schemas';
import { hasInvalidKeyValueCredentials } from './CredentialKeyValueInput';
import {
  EmptyCredentialValuesConfirmDialog,
  useEmptyCredentialValuesConfirmation,
} from './EmptyCredentialValuesConfirmDialog';
import {
  buildConfigMapFormDefaults,
  classifyDuplicateCredentialsError,
  findKeyValueAuthentication,
  getDuplicateConnectionError,
  getConfigMapInitialValues,
  getConnectionInitialValues,
  getCredentialsForAuthMethod,
  getKeyValueCredentialIds,
  hasConfigMap,
  keepUntouchedSavedKeyValueCredentials,
  mapConfigMapToApiFormat,
  promoteDraftToProductionConnection,
  resolveConnectionLabel,
} from '../utils';
import { useWaitForConnectionToBeReady } from '../hooks/useWaitForConnectionToBeReady';
import { ConnectionStatusTag } from './ConnectionStatusTag';
import { isConnectionInactive } from '../hooks/use-reactivate-connection';
import {
  ConnectionDiagnosticsPopover,
  shouldShowHealthDetails,
  hasConnectionFailure,
} from './ConnectionDiagnostics';
import { useConnectionDiagnosticsInsight } from '../hooks/use-connection-diagnostics-insight';
import { IntegrationAssistantBanner } from './IntegrationAssistantBanner';
import { IntegrationAssistantPanel } from './IntegrationAssistantPanel';
import { ManagedConnectionCredential } from './ManagedConnectionCredential';
import { useSharedAuthentications } from '../hooks/use-shared-authentications';
import { useRemoteSecretConnectionActions } from '../hooks/use-remote-secret-connection-actions';
import { useConnectionActions } from '../hooks/useConnectionActions';
import {
  defaultCredentialDisplayName,
  useAbandonOAuthSignIn,
  writePendingOAuthConnection,
  type IOAuthConnectionResume,
} from '@/modules/credentials';
import { validateOAuthRedirectUrl } from '@/shared/utils';

export interface IEditConnectionDialogProps {
  showSuccessToast?: boolean;
  integration: Bdkv2BookDescriptor;
  connections: V2BookConnectionDescriptor[];
  isOpen: boolean;
  updateConnection: (
    connection: V2BookConnectionDescriptor,
    credentials: Record<string, string>,
    integration: Bdkv2BookDescriptor,
    configMap?: Record<string, Bdkv2Value>,
  ) => Promise<V2BookConnectionResponse>;
  createConnection: (
    integration: Bdkv2BookDescriptor,
    connectionName: string,
    authMethodId: string,
    credentials: Record<string, string>,
    labels: string[],
    oauthFlow?: Bdkv2OAuthFlow,
    configMap?: Record<string, Bdkv2Value>,
  ) => Promise<V2BookConnectionResponse>;
  onSuccess: (
    draftConnection?: V2BookConnectionDescriptor,
    publishedConnection?: V2BookConnectionDescriptor,
  ) => void;
  onCancel: () => void;
  publishOnlyMode?: boolean;
  draftOnlyMode?: boolean;
  /**
   * Names of other connections in the workspace, used to enforce the same
   * uniqueness validation as the create flow when renaming. The current
   * connection's own name is excluded internally so it can be kept as-is.
   */
  existingConnectionNames?: string[];
  /**
   * When true, the integration book for the connection's own version is still
   * being fetched. The dialog opens immediately but shows a spinner in place of
   * the form until it resolves, so the fields always match the connection's
   * version rather than a stale one.
   */
  isLoadingIntegration?: boolean;
  /** Whether the host reopens this dialog after an OAuth sign-in redirect. */
  supportsOAuthResume?: boolean;
  /** Back from signing a new credential in: the credential to use, or the error. */
  oauthResume?: IOAuthConnectionResume | null;
}

// Stable default so the prop's identity doesn't change between renders.
const EMPTY_CONNECTION_NAMES: string[] = [];

export function EditConnectionDialog({
  showSuccessToast = true,
  integration,
  connections,
  isOpen,
  updateConnection,
  createConnection,
  onSuccess,
  onCancel,
  publishOnlyMode = false,
  draftOnlyMode = false,
  existingConnectionNames = EMPTY_CONNECTION_NAMES,
  isLoadingIntegration = false,
  supportsOAuthResume = false,
  oauthResume = null,
}: IEditConnectionDialogProps) {
  'use no memo';
  const { t: tIntegrations } = useTranslation('integrations');
  const { t: tCommon } = useTranslation('common');
  const { t: tCredentials } = useTranslation('credentials');

  const isAssistantEnabled = useGateValue('enable_integration_assistant');
  const [openSidePanel, setOpenSidePanel] = useState<'assistant' | null>(null);
  const isAssistantOpen = openSidePanel === 'assistant';
  const [wasOpen, setWasOpen] = useState(false);
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const [isLoading, setIsLoading] = useState(false);
  const [isWaitingForReady, setIsWaitingForReady] = useState(false);
  const [isConnectionTakingTooLong, setIsConnectionTakingTooLong] =
    useState(false);
  // When set, the diagnostics panel takes over the dialog to reflect the
  // result of the just-submitted update (checking -> healthy/failed), mirroring
  // the Add dialog. Only used when the integration assistant is enabled.
  const [panelConnection, setPanelConnection] = useState<
    V2BookConnectionDescriptor | undefined
  >(undefined);
  const successTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Tracks whether the user closed the dialog (e.g. via ESC) while the
  // ready-poll was in flight. If they did, we must not call `onSuccess`
  // when the poll eventually resolves — otherwise the parent would open
  // ConfigureActionsDialog even though the user explicitly bailed.
  const waitCancelledRef = useRef(false);

  useEffect(() => {
    if (!isWaitingForReady) {
      setIsConnectionTakingTooLong(false);
      return;
    }
    const timer = setTimeout(() => setIsConnectionTakingTooLong(true), 30_000);
    return () => clearTimeout(timer);
  }, [isWaitingForReady]);
  const waitForConnectionToBeReady = useWaitForConnectionToBeReady();

  // Clear the pending success handoff if the dialog unmounts mid-timer.
  useEffect(() => () => clearTimeout(successTimerRef.current), []);

  // Find connections by type
  const draftConnection = connections.find(conn =>
    conn.labels?.some(
      label => label.key === 'draft_connection' && label.value === 'true',
    ),
  );

  const publishedConnection = connections.find(conn =>
    conn.labels?.some(
      label => label.key === 'published_connection' && label.value === 'true',
    ),
  );

  // Check if this is a single connection with both labels
  const singleConnection = connections.find(conn => {
    const hasDraft = conn.labels?.some(
      label => label.key === 'draft_connection' && label.value === 'true',
    );
    const hasPublished = conn.labels?.some(
      label => label.key === 'published_connection' && label.value === 'true',
    );
    return hasDraft && hasPublished;
  });

  const isSingleConnectionMode = !!singleConnection;

  // Determine which connection to use for metadata
  const primaryConnection =
    singleConnection || draftConnection || publishedConnection;

  // Decided once per open (not on every status change), so a failure that
  // lands mid-edit doesn't pop the assistant over the form.
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    const opensOnFailure =
      isOpen &&
      isAssistantEnabled &&
      !!primaryConnection?.connection_id &&
      hasConnectionFailure(primaryConnection.status);
    setOpenSidePanel(opensOnFailure ? 'assistant' : null);
  }

  // The connection whose diagnostics the panel reflects: the live submit result
  // (checking/healthy/failed) when one is in flight, otherwise the current
  // connection so its status is visible the whole time the dialog is open.
  const diagnosedConnection = panelConnection ?? primaryConnection;

  // An inactive connection has no running pod to validate against, so BDK will
  // accept the edit but report nothing meaningful about it. Warn up front and
  // point at reactivation rather than letting the user read a stale status as
  // the verdict on credentials they just changed.
  const hasInactiveConnection = connections.some(isConnectionInactive);
  const diagnosisInsight = useConnectionDiagnosticsInsight({
    connection: diagnosedConnection,
    displayName: integration.display_name || integration.name,
    enabled:
      isOpen &&
      isAssistantEnabled &&
      hasConnectionFailure(diagnosedConnection?.status),
  });
  const diagnosedStatusTag = (
    <ConnectionStatusTag
      connections={diagnosedConnection ? [diagnosedConnection] : []}
      status={
        diagnosedConnection?.status?.state || 'BOOK_CONNECTION_STATE_PENDING'
      }
      style="badge"
    />
  );
  const completedDiagnosis = diagnosisInsight.isLoading
    ? undefined
    : diagnosisInsight.insight || undefined;
  const rawAuthenticationId =
    primaryConnection?.authentication?.authentication_id;

  // Fallback: only for integrations with a key/value credential (KV editor pattern)
  const fallbackCustomAuth = !rawAuthenticationId
    ? findKeyValueAuthentication(integration)
    : undefined;
  const isNoAuthFallback = !rawAuthenticationId && !!fallbackCustomAuth;
  const effectiveSingleMode = isSingleConnectionMode && !isNoAuthFallback;

  const authenticationId =
    rawAuthenticationId || fallbackCustomAuth?.custom?.id || '';
  const isOAuthType = authenticationId?.startsWith('oauth');
  const connectionName = primaryConnection?.connection_name;

  // Exclude the current connection's own name so the user can keep it while
  // still validating uniqueness against every other connection in the workspace.
  const otherConnectionNames = useMemo(
    () => existingConnectionNames.filter(name => name !== connectionName),
    [existingConnectionNames, connectionName],
  );

  // Look up the auth method to get the OAuth flow
  const selectedOAuthAuth = useMemo(() => {
    if (!authenticationId || !isOAuthType) return null;
    return integration.authentications?.find(
      auth => auth.oauth?.id === authenticationId,
    );
  }, [authenticationId, isOAuthType, integration.authentications]);

  const selectedOAuthFlow = selectedOAuthAuth?.oauth?.flows?.[0];

  // Check if OAuth authorization code flow by checking flows array
  const isOAuthAuthCode = selectedOAuthAuth?.oauth?.flows?.includes(
    Bdkv2OAuthFlow.OAuthFlowAuthorizationCode,
  );

  // Find the selected custom auth method (for non-OAuth connections)
  const selectedCustomAuth = useMemo(
    () =>
      integration.authentications?.find(
        auth => auth.custom?.id === authenticationId,
      ),
    [authenticationId, integration.authentications],
  );

  // The connection picks the editor; the gate only allows changing the credential.
  const isManagedCredentialsEnabled = useGateValue(
    FEATURE_FLAGS.ENABLE_GRINGOTTS,
  );
  const connectionAuthentication = rawAuthenticationId
    ? integration.authentications?.find(
        auth => (auth.custom?.id || auth.oauth?.id) === rawAuthenticationId,
      )
    : undefined;
  const usesManagedCredentials =
    connections.some(connection => !!connection.credentials_id) &&
    !!connectionAuthentication?.secret_type_id;
  const canChangeCredential =
    usesManagedCredentials && isManagedCredentialsEnabled;
  const sharedAuth = useSharedAuthentications(
    organizationId,
    workspaceId,
    integration,
    canChangeCredential && isOpen,
  );
  const sharedApp = [...sharedAuth.entries.values()].flatMap(entry =>
    entry.kind === 'oauthApp' && entry.rawAuthMethodId === rawAuthenticationId
      ? [entry.app]
      : [],
  )[0];
  const ownOAuthApps =
    sharedAuth.ownAppsByType.get(
      connectionAuthentication?.secret_type_id ?? '',
    ) ?? [];
  const { createConnectionWithRemoteSecret, saveConnectionWithRemoteSecret } =
    useRemoteSecretConnectionActions(organizationId, workspaceId);
  const { updateConnectionLabels } = useConnectionActions(
    organizationId,
    workspaceId,
  );
  const abandonSignIn = useAbandonOAuthSignIn(organizationId, workspaceId);

  // Credentials picked in this edit, per environment; unset keeps the current one.
  const [chosenCredentialIds, setChosenCredentialIds] = useState<
    Partial<Record<EnvironmentType, string>>
  >({});
  const hasCredentialChanges =
    Object.values(chosenCredentialIds).some(Boolean) ||
    // Creating Production with the Test credential needs no pick (gate off).
    (publishOnlyMode &&
      !publishedConnection &&
      usesManagedCredentials &&
      !canChangeCredential);
  // Environments whose credential has values typed in but not saved; they're
  // saved from the credential, not with the connection.
  const [unsavedCredentialEdits, setUnsavedCredentialEdits] = useState<
    Partial<Record<EnvironmentType, boolean>>
  >({});
  // Hosts keep the dialog mounted while closed, so a pick mustn't outlive it.
  const [wasOpenForCredentials, setWasOpenForCredentials] = useState(isOpen);
  if (isOpen !== wasOpenForCredentials) {
    setWasOpenForCredentials(isOpen);
    if (!isOpen) {
      setChosenCredentialIds({});
      setUnsavedCredentialEdits({});
    }
  }
  // Hosts may pass a resume for another connection; only this one's applies.
  const editResume = oauthResume?.edit?.connectionIds.some(id =>
    connections.some(connection => connection.connection_id === id),
  )
    ? oauthResume
    : null;
  const resumeEnvironment = editResume?.edit
    ? editResume.edit.environment === 'published'
      ? EnvironmentType.Published
      : EnvironmentType.Draft
    : undefined;
  const [appliedResume, setAppliedResume] =
    useState<IOAuthConnectionResume | null>(null);
  if (editResume && resumeEnvironment && editResume !== appliedResume) {
    setAppliedResume(editResume);
    if (!editResume.error) {
      setChosenCredentialIds({ [resumeEnvironment]: editResume.credentialId });
    }
  }
  const resumeError = editResume?.error
    ? tCredentials('credentials.oauth.consentFailed')
    : undefined;

  // Get credentials for the authentication method. A managed credential's
  // values live in the credential itself, not in this form.
  const credentials = useMemo(
    () =>
      usesManagedCredentials
        ? []
        : getCredentialsForAuthMethod(
            selectedOAuthFlow,
            selectedCustomAuth,
            integration.authentications,
          ),
    [
      usesManagedCredentials,
      selectedOAuthFlow,
      selectedCustomAuth,
      integration.authentications,
    ],
  );

  // Check if integration has config_map
  const integrationHasConfigMap = useMemo(
    () => hasConfigMap(integration),
    [integration],
  );

  // Create config_map schema
  const configMapSchema = useMemo(
    () => createConfigMapSchema(integration.config_map),
    [integration.config_map],
  );

  // Create schema and form - combine credentials and config_map
  // In single connection mode, publishedConfig is never shown/used, so make it optional
  const credentialsSchema = useMemo(
    () => createAuthenticationCredentialsSchema(credentials),
    [credentials],
  );
  const schema = useMemo(
    () =>
      z.object({
        activeEnvironment: z.string(),
        connectionName: createConnectionNameValidation(otherConnectionNames),
        syncCredentials: z.boolean(),
        draftConfig: credentialsSchema,
        publishedConfig: effectiveSingleMode
          ? credentialsSchema.partial()
          : credentialsSchema,
        ...(integrationHasConfigMap
          ? {
              draftConfigMap: configMapSchema,
              publishedConfigMap: configMapSchema,
            }
          : {}),
      }),
    [
      credentialsSchema,
      effectiveSingleMode,
      integrationHasConfigMap,
      configMapSchema,
      otherConnectionNames,
    ],
  );

  // Get initial config_map values from connections
  const draftConfigMapInitial = useMemo(
    () =>
      getConfigMapInitialValues(
        effectiveSingleMode ? singleConnection : draftConnection,
      ),
    [effectiveSingleMode, singleConnection, draftConnection],
  );

  const publishedConfigMapInitial = useMemo(
    () =>
      getConfigMapInitialValues(
        effectiveSingleMode ? singleConnection : publishedConnection,
      ),
    [effectiveSingleMode, singleConnection, publishedConnection],
  );

  // Changes made before leaving to sign in come back as unsaved changes.
  const resumedConnectionName = editResume?.connectionName;
  const resumedConfigMaps = editResume?.edit?.configMaps;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic form with credentials + optional configMap
  const form = useForm<any>({
    resolver: zodResolver(schema),
    defaultValues: {
      activeEnvironment:
        resumeEnvironment ??
        (publishOnlyMode
          ? EnvironmentType.Published
          : draftOnlyMode
            ? EnvironmentType.Draft
            : draftConnection
              ? EnvironmentType.Draft
              : EnvironmentType.Published),
      connectionName: connectionName ?? '',
      syncCredentials: isNoAuthFallback,
      draftConfig: getConnectionInitialValues(
        effectiveSingleMode ? singleConnection : draftConnection,
      ),
      publishedConfig: getConnectionInitialValues(
        effectiveSingleMode ? singleConnection : publishedConnection,
      ),
      ...(integrationHasConfigMap
        ? {
            draftConfigMap:
              draftConfigMapInitial.configMap ||
              buildConfigMapFormDefaults(
                integration.config_map,
                draftConfigMapInitial,
              ),
            publishedConfigMap:
              publishedConfigMapInitial.configMap ||
              buildConfigMapFormDefaults(
                integration.config_map,
                publishedConfigMapInitial,
              ),
          }
        : {}),
    },
  });

  const { isDirty } = form.formState;

  // Watch form values
  const activeEnvironment = form.watch('activeEnvironment') as EnvironmentType;
  const syncCredentials = form.watch('syncCredentials');
  const draftValues = form.watch('draftConfig');
  const draftConfigMapValues = form.watch('draftConfigMap');

  // Implement real-time sync: when sync is enabled, copy draft values to published
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync draft credentials to published when sync is enabled
  useEffect(() => {
    if (syncCredentials && draftValues) {
      Object.keys(draftValues).forEach(key => {
        form.setValue(`publishedConfig.${key}`, draftValues[key]);
      });
    }
    // Also sync config_map if present
    if (syncCredentials && integrationHasConfigMap && draftConfigMapValues) {
      Object.keys(draftConfigMapValues).forEach(key => {
        form.setValue(`publishedConfigMap.${key}`, draftConfigMapValues[key]);
      });
    }
  }, [
    syncCredentials,
    draftValues,
    draftConfigMapValues,
    integrationHasConfigMap,
    form,
  ]);

  // Reset form when connections change
  // eslint-disable-next-line no-restricted-syntax -- reason: Reset form when connections change
  useEffect(() => {
    const newDraftConfigMap = getConfigMapInitialValues(
      effectiveSingleMode ? singleConnection : draftConnection,
    );
    const newPublishedConfigMap = getConfigMapInitialValues(
      effectiveSingleMode ? singleConnection : publishedConnection,
    );

    form.reset({
      activeEnvironment:
        resumeEnvironment ??
        (publishOnlyMode
          ? EnvironmentType.Published
          : draftOnlyMode
            ? EnvironmentType.Draft
            : draftConnection
              ? EnvironmentType.Draft
              : EnvironmentType.Published),
      connectionName: connectionName ?? '',
      syncCredentials: isNoAuthFallback,
      draftConfig: getConnectionInitialValues(
        effectiveSingleMode ? singleConnection : draftConnection,
      ),
      publishedConfig: getConnectionInitialValues(
        effectiveSingleMode ? singleConnection : publishedConnection,
      ),
      ...(integrationHasConfigMap
        ? {
            draftConfigMap: buildConfigMapFormDefaults(
              integration.config_map,
              newDraftConfigMap,
            ),
            publishedConfigMap: buildConfigMapFormDefaults(
              integration.config_map,
              newPublishedConfigMap,
            ),
          }
        : {}),
    });
    const keepChange = (fieldName: string, value: unknown) =>
      form.setValue(fieldName, value, { shouldDirty: true });
    if (resumedConnectionName && resumedConnectionName !== connectionName) {
      keepChange('connectionName', resumedConnectionName);
    }
    if (integrationHasConfigMap && resumedConfigMaps?.draft) {
      keepChange('draftConfigMap', resumedConfigMaps.draft);
    }
    if (integrationHasConfigMap && resumedConfigMaps?.published) {
      keepChange('publishedConfigMap', resumedConfigMaps.published);
    }
  }, [
    draftConnection,
    publishedConnection,
    singleConnection,
    effectiveSingleMode,
    isNoAuthFallback,
    publishOnlyMode,
    draftOnlyMode,
    integrationHasConfigMap,
    integration.config_map,
    connectionName,
    resumeEnvironment,
    resumedConnectionName,
    resumedConfigMaps,
    form,
  ]);

  const getNextConnectionName = () =>
    (form.getValues('connectionName') ?? connectionName ?? '').trim();

  const handleCancel = () => {
    setChosenCredentialIds({});
    setUnsavedCredentialEdits({});
    clearTimeout(successTimerRef.current);
    setPanelConnection(undefined);
    setOpenSidePanel(null);
    form.reset();
    resetReuseAlert();
    onCancel();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Dialog is being closed - reset the state. Also flag any in-flight
      // ready-poll as cancelled so onSuccess doesn't fire after the user
      // dismissed the dialog (e.g. via ESC during the wait).
      waitCancelledRef.current = true;
      setChosenCredentialIds({});
      setUnsavedCredentialEdits({});
      clearTimeout(successTimerRef.current);
      setPanelConnection(undefined);
      setOpenSidePanel(null);
      setIsWaitingForReady(false);
      form.reset();
      resetReuseAlert();
      onCancel();
    }
  };

  const handleEnvironmentChange = (value: string) => {
    form.setValue('activeEnvironment', value);
    // Switching tabs means the user is changing what they intend to submit,
    // so the previous publish-create duplicate (if any) no longer applies —
    // dropping the alert also re-enables the Save button so they can resubmit.
    resetReuseAlert();
  };

  /** The connection an environment's credential belongs to. */
  const connectionFor = (environment: EnvironmentType) =>
    effectiveSingleMode
      ? singleConnection
      : environment === EnvironmentType.Draft
        ? draftConnection
        : publishedConnection;

  /**
   * The credential an environment's connection has. A new Production
   * connection gets the Test one's when the credential can't be chosen.
   */
  const currentCredentialIdFor = (environment: EnvironmentType) => {
    const connection = connectionFor(environment);
    if (connection || canChangeCredential) return connection?.credentials_id;
    return draftConnection?.credentials_id;
  };

  /** The credential an environment saves with: the one picked, else the current one. */
  const credentialIdFor = (environment: EnvironmentType) =>
    chosenCredentialIds[environment] ?? currentCredentialIdFor(environment);

  /** Whether an environment's existing connection has credentials set inline. */
  const hasInlineCredentials = (environment: EnvironmentType) => {
    const connection = connectionFor(environment);
    return !!connection && !connection.credentials_id;
  };

  /** Whether an environment keeps its inline credentials: none was picked for it. */
  const keepsInlineCredentials = (environment: EnvironmentType) =>
    hasInlineCredentials(environment) && !chosenCredentialIds[environment];

  /** The environments whose connection the active mode saves. */
  const savedEnvironments = (): EnvironmentType[] => {
    if (draftOnlyMode || effectiveSingleMode) return [EnvironmentType.Draft];
    if (publishOnlyMode) return [EnvironmentType.Published];
    return [
      ...(draftConnection ? [EnvironmentType.Draft] : []),
      EnvironmentType.Published,
    ];
  };

  /**
   * Sends the browser to the provider to sign a new credential in, saving the
   * edit so the dialog reopens on return.
   */
  const redirectToConsent = (
    environment: EnvironmentType,
    authorizeUrl: string,
    credentialId: string,
  ): boolean => {
    try {
      validateOAuthRedirectUrl(authorizeUrl);
    } catch (error) {
      abandonSignIn(credentialId, error);
      toast.error(tIntegrations('integrations.addConnection.oauthError'));
      return false;
    }
    const configName =
      environment === EnvironmentType.Draft
        ? 'draftConfigMap'
        : 'publishedConfigMap';
    const configMap =
      integrationHasConfigMap && form.getValues(configName)
        ? mapConfigMapToApiFormat(
            form.getValues(configName),
            integration.config_map,
          )
        : undefined;
    const connection = connectionFor(environment);
    // Without a dialog to reopen, the connection is written on return, as
    // BDK's own sign-in does.
    const connectOnReturn = supportsOAuthResume
      ? undefined
      : connection?.connection_id
        ? {
            action: 'update' as const,
            connectionId: connection.connection_id,
            connectionName: getNextConnectionName(),
            authMethodId: rawAuthenticationId || '',
            labels: connection.labels ?? [],
            configMap,
          }
        : {
            action: 'create' as const,
            connectionName: getNextConnectionName(),
            authMethodId: rawAuthenticationId || '',
            labels: ['published'],
            configMap,
          };
    writePendingOAuthConnection({
      ...(connectOnReturn ? { connectOnReturn } : {}),
      organizationId,
      workspaceId,
      integrationName: integration.name || '',
      integrationVersion: integration.version || '',
      connectionName: getNextConnectionName(),
      authMethodId: rawAuthenticationId || '',
      credentialId,
      edit: {
        connectionIds: connections.flatMap(connection =>
          connection.connection_id ? [connection.connection_id] : [],
        ),
        environment:
          environment === EnvironmentType.Published ? 'published' : 'draft',
        ...(integrationHasConfigMap
          ? {
              configMaps: {
                draft: form.getValues('draftConfigMap'),
                published: form.getValues('publishedConfigMap'),
              },
            }
          : {}),
      },
    });
    window.location.assign(authorizeUrl);
    return true;
  };

  /**
   * Checks the fields the active mode saves, surfacing a toast (and the
   * offending environment tab) when they are invalid.
   *
   * @returns Whether the save can proceed
   */
  const validateSave = async (): Promise<boolean> => {
    if (Object.values(unsavedCredentialEdits).some(Boolean)) {
      toast.error(tCredentials('credentials.connection.unsavedEdit'));
      return false;
    }
    if (usesManagedCredentials) {
      const missing = savedEnvironments().find(
        environment =>
          !credentialIdFor(environment) && !keepsInlineCredentials(environment),
      );
      if (missing) {
        if (!effectiveSingleMode) form.setValue('activeEnvironment', missing);
        toast.error(tCredentials('credentials.connection.required'));
        return false;
      }
    }

    // For no-auth fallback, check if any credentials were actually entered
    if (isNoAuthFallback) {
      const draftConfig = form.getValues('draftConfig');
      const hasAnyCredentials =
        draftConfig &&
        Object.values(draftConfig).some(
          v => v !== undefined && v !== null && String(v).trim() !== '',
        );
      if (!hasAnyCredentials) {
        toast.error(
          tIntegrations(
            'integrations.editConnection.noAuthFallback.emptyCredentials',
          ),
        );
        return false;
      }
    }

    // Validate only the fields relevant to the active mode
    const fieldsToValidate: string[] =
      syncCredentials || draftOnlyMode
        ? [
            'connectionName',
            'draftConfig',
            'activeEnvironment',
            'syncCredentials',
            ...(integrationHasConfigMap ? ['draftConfigMap'] : []),
          ]
        : publishOnlyMode
          ? [
              'connectionName',
              'publishedConfig',
              'activeEnvironment',
              'syncCredentials',
              ...(integrationHasConfigMap ? ['publishedConfigMap'] : []),
            ]
          : [
              'connectionName',
              'draftConfig',
              'publishedConfig',
              'activeEnvironment',
              'syncCredentials',
              ...(integrationHasConfigMap
                ? ['draftConfigMap', 'publishedConfigMap']
                : []),
            ];

    // Validate the form (specific fields when synced, all fields otherwise)
    const isValid =
      fieldsToValidate.length > 0
        ? await form.trigger(fieldsToValidate)
        : await form.trigger();
    if (!isValid) {
      // The offending field can live on the inactive environment tab, where its
      // inline error is hidden — so a silent return looks like "Save does
      // nothing". Reveal that tab and surface a toast so the failure is visible.
      const { errors } = form.formState;
      const draftHasError = !!errors.draftConfig || !!errors.draftConfigMap;
      const publishedHasError =
        !!errors.publishedConfig || !!errors.publishedConfigMap;
      const active = form.getValues('activeEnvironment');
      if (
        draftHasError &&
        !publishedHasError &&
        active !== EnvironmentType.Draft
      ) {
        form.setValue('activeEnvironment', EnvironmentType.Draft);
      } else if (
        publishedHasError &&
        !draftHasError &&
        active !== EnvironmentType.Published
      ) {
        form.setValue('activeEnvironment', EnvironmentType.Published);
      }
      toast.error(tIntegrations('integrations.editConnection.validationError'));
      return false;
    }

    return true;
  };

  const saveConnection = async () => {
    // Get values from form
    const formValues = form.getValues();

    // The (possibly renamed) connection name to persist. updateConnection reads
    // the name off the descriptor it's handed, so we override it on every
    // descriptor below to keep the draft/published pair in sync.
    const nextConnectionName = getNextConnectionName();
    const withName = (
      connection: V2BookConnectionDescriptor,
    ): V2BookConnectionDescriptor => ({
      ...connection,
      connection_name: nextConnectionName,
    });

    const credentialsFor = (
      connection: V2BookConnectionDescriptor,
      configName: 'draftConfig' | 'publishedConfig',
    ) =>
      keepUntouchedSavedKeyValueCredentials(
        formValues[configName],
        connection,
        keyValueCredentialIds,
        id => form.getFieldState(`${configName}.${id}`).isDirty,
      );

    /** Saves a connection: re-pointed at its credential, or with inline values. */
    const update = (
      connection: V2BookConnectionDescriptor,
      configName: 'draftConfig' | 'publishedConfig',
      configMap?: Record<string, Bdkv2Value>,
    ) => {
      const environment =
        configName === 'draftConfig'
          ? EnvironmentType.Draft
          : EnvironmentType.Published;
      // Its fields aren't shown here, so only the name and configuration
      // change; echoing its authentication keeps the stored values.
      if (usesManagedCredentials && keepsInlineCredentials(environment)) {
        return updateConnectionLabels(
          { ...connection, config_map: configMap ?? connection.config_map },
          connection.labels ?? [],
        );
      }
      return usesManagedCredentials
        ? saveConnectionWithRemoteSecret({
            integration,
            connection,
            credentialId: credentialIdFor(environment) ?? '',
            configMap,
          })
        : updateConnection(
            connection,
            credentialsFor(connection, configName),
            integration,
            configMap,
          );
    };

    /** Creates the Production connection the edit adds. */
    const createPublished = (configMap?: Record<string, Bdkv2Value>) =>
      usesManagedCredentials
        ? createConnectionWithRemoteSecret({
            integration,
            connectionName: nextConnectionName,
            authMethodId: authenticationId,
            credentialId: credentialIdFor(EnvironmentType.Published) ?? '',
            labels: ['published'],
            configMap,
          })
        : createConnection(
            integration,
            nextConnectionName,
            authenticationId!,
            formValues.publishedConfig,
            ['published'],
            selectedOAuthFlow,
            configMap,
          );

    // Convert config_map form values to API format
    const draftConfigMapApi =
      integrationHasConfigMap && formValues.draftConfigMap
        ? mapConfigMapToApiFormat(
            formValues.draftConfigMap,
            integration.config_map,
          )
        : undefined;

    const publishedConfigMapApi =
      integrationHasConfigMap && formValues.publishedConfigMap
        ? mapConfigMapToApiFormat(
            formValues.publishedConfigMap,
            integration.config_map,
          )
        : undefined;

    try {
      setIsLoading(true);
      waitCancelledRef.current = false;

      // Clear any previous diagnostics panel so a resubmit restarts cleanly.
      clearTimeout(successTimerRef.current);
      setPanelConnection(undefined);

      let finalDraftConnection = draftConnection;
      let finalPublishedConnection = publishedConnection;

      if (draftOnlyMode) {
        // Draft-only mode: only update draft connection
        if (draftConnection) {
          const draftResponse = await update(
            withName(draftConnection),
            'draftConfig',
            draftConfigMapApi,
          );
          finalDraftConnection = draftResponse.book_connection_descriptor;
        }
      } else if (publishOnlyMode) {
        // Publish-only mode: update the published connection if it already exists otherwise create one.
        if (publishedConnection) {
          const publishedResponse = await update(
            withName(publishedConnection),
            'publishedConfig',
            publishedConfigMapApi,
          );
          finalPublishedConnection =
            publishedResponse.book_connection_descriptor;
        } else if (draftConnection) {
          const publishedResponse = await createPublished(
            publishedConfigMapApi,
          );
          finalPublishedConnection =
            publishedResponse.book_connection_descriptor;
        }
      } else if (isNoAuthFallback && singleConnection) {
        // No-auth fallback: single connection with both labels, no original auth
        if (syncCredentials) {
          // Sync ON: update single connection (keep both labels)
          const singleResponse = await update(
            withName(singleConnection),
            'draftConfig',
            draftConfigMapApi,
          );
          const updated = singleResponse.book_connection_descriptor;
          finalDraftConnection = updated;
          finalPublishedConnection = updated;
        } else {
          // Sync OFF: split into draft-only + new published
          const draftOnlyConnection = withName({
            ...singleConnection,
            labels: singleConnection.labels?.filter(
              l => l.key !== 'published_connection',
            ),
          });
          const draftResponse = await update(
            draftOnlyConnection,
            'draftConfig',
            draftConfigMapApi,
          );
          finalDraftConnection = draftResponse.book_connection_descriptor;

          const publishedResponse = await createPublished(
            publishedConfigMapApi,
          );
          finalPublishedConnection =
            publishedResponse.book_connection_descriptor;
        }
      } else if (isSingleConnectionMode && singleConnection) {
        // Single connection mode - update the single connection
        // Use draftConfig since both environments use the same credentials
        const singleResponse = await update(
          withName(singleConnection),
          'draftConfig',
          draftConfigMapApi,
        );
        const updated = singleResponse.book_connection_descriptor;
        finalDraftConnection = updated;
        finalPublishedConnection = updated;
      } else {
        // Separate connection mode - update or create connections
        if (draftConnection) {
          const draftResponse = await update(
            withName(draftConnection),
            'draftConfig',
            draftConfigMapApi,
          );
          finalDraftConnection = draftResponse.book_connection_descriptor;
        }

        if (publishedConnection) {
          const publishedResponse = await update(
            withName(publishedConnection),
            'publishedConfig',
            publishedConfigMapApi,
          );
          finalPublishedConnection =
            publishedResponse.book_connection_descriptor;
        } else if (draftConnection && !effectiveSingleMode) {
          // Create published connection if it doesn't exist
          const publishedResponse = await createPublished(
            publishedConfigMapApi,
          );
          finalPublishedConnection =
            publishedResponse.book_connection_descriptor;
        }
      }

      const connectionsFailed = [
        finalDraftConnection,
        finalPublishedConnection,
      ].some(
        conn =>
          conn?.status?.state ===
          V2BookConnectionState.BOOK_CONNECTION_STATE_FAILED,
      );

      if (connectionsFailed) {
        toast.error(
          tIntegrations('integrations.editConnection.validationFailed'),
        );
      } else if (showSuccessToast) {
        // Saying "updated successfully" on an inactive connection would imply the
        // new credentials were checked. They weren't — name the next step instead.
        toast.success(
          hasInactiveConnection
            ? tIntegrations('integrations.editConnection.inactiveSaved')
            : tIntegrations('integrations.editConnection.success'),
        );
      }

      // For discover-capable integrations, hold the dialog open until the
      // connection's deployment has settled at the new spec. The caller can
      // then hand the freshly-updated connection straight to the
      // ConfigureActionsDialog so the user can review/refresh entities
      // without having to dig back into the connection.
      // Post-rollout descriptors keyed by connection id. The update mutation's
      // response is captured *before* the readiness poll, so its `status.state`
      // can be stale (e.g. PENDING for a connection that settles back to
      // INACTIVE). Callers gate the ConfigureActionsDialog handoff on that
      // state, so hand them what the poll actually observed.
      const polledById = new Map<string, V2BookConnectionDescriptor>();
      const withPolledState = (conn?: V2BookConnectionDescriptor) =>
        (conn?.connection_id
          ? polledById.get(conn.connection_id)
          : undefined) ?? conn;

      if (integration.discover_capable) {
        const uniqueConnectionIds = [
          finalDraftConnection?.connection_id,
          finalPublishedConnection?.connection_id,
        ].filter(
          (id, index, arr): id is string => !!id && arr.indexOf(id) === index,
        );
        if (uniqueConnectionIds.length > 0) {
          // With the assistant on, surface the live diagnostics inline (the
          // checking state) instead of the plain "connecting" overlay.
          if (isAssistantEnabled) {
            setPanelConnection(
              finalDraftConnection ?? finalPublishedConnection,
            );
          }
          setIsWaitingForReady(true);
          try {
            const results = await Promise.all(
              uniqueConnectionIds.map(id => waitForConnectionToBeReady(id)),
            );

            // The user closed the dialog mid-wait — don't resurface anything.
            if (waitCancelledRef.current) {
              return;
            }

            results.forEach(result => {
              const id = result.connection?.connection_id;
              if (id && result.connection)
                polledById.set(id, result.connection);
            });

            if (isAssistantEnabled) {
              // An inactive result is not a failure: the write landed, it just
              // can't be validated while the pod is scaled to zero. Treating it
              // as failed would keep the dialog open showing a stale diagnosis
              // of credentials the user already fixed.
              const failed = results.find(r => !r.isReady && !r.isInactive);
              if (failed) {
                // Keep the dialog open and re-show the fresh (post-update)
                // diagnosis so the user can correct and resubmit.
                setPanelConnection(failed.connection);
                setIsWaitingForReady(false);
                return;
              }
              // Success: show the healthy state briefly, then hand off.
              setPanelConnection(results[0]?.connection);
              setIsWaitingForReady(false);
              successTimerRef.current = setTimeout(() => {
                onSuccess(
                  withPolledState(finalDraftConnection),
                  withPolledState(finalPublishedConnection),
                );
                form.reset();
                setPanelConnection(undefined);
              }, 1500);
              return;
            }
          } catch (waitError) {
            captureException(
              waitError instanceof Error
                ? waitError
                : new Error(String(waitError)),
              {
                tags: {
                  component: 'EditConnectionDialog',
                  action: 'waitForConnectionToBeReady',
                },
              },
            );
          } finally {
            setIsWaitingForReady(false);
          }
        }
      }

      // If the user closed the dialog while we were waiting for readiness,
      // skip the success handoff so the parent doesn't auto-open
      // ConfigureActionsDialog on a dialog the user explicitly bailed out of.
      if (!waitCancelledRef.current) {
        onSuccess(
          withPolledState(finalDraftConnection),
          withPolledState(finalPublishedConnection),
        );
        form.reset();
      }
    } catch (error) {
      const classification = classifyDuplicateCredentialsError(
        error,
        draftConnection?.connection_id,
      );
      if (classification.kind === 'ownDraftDuplicate') {
        // Expected: published credentials match this connection's own draft, so
        // we offer to reuse it. Not an error worth reporting.
        setReuseAlertSnapshot({ ...formValues.publishedConfig });
      } else if (classification.kind === 'otherConnectionDuplicate') {
        // Expected: credentials already belong to another connection. Surfaced
        // with a dedicated toast naming it; not reported.
        toast.error(
          tIntegrations('integrations.addConnection.duplicateCredentials', {
            connectionName: resolveConnectionLabel(
              connections,
              classification.existingId,
            ),
          }),
        );
      } else if (getDuplicateConnectionError(error)?.reason === 'name') {
        // Expected: the connection name is already taken for this
        // book/version/labels. Prompt for a different name; not reported.
        toast.error(tIntegrations('integrations.addConnection.duplicateName'));
      } else {
        // Genuine, unexpected failure — surface and report.
        toast.error(tIntegrations('integrations.editConnection.error'));
        captureException(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReuseTestConnection = async () => {
    // Reuse only fires from the Published-tab path where draftConnection exists
    // and no separate published connection has been created yet.
    if (!draftConnection || publishedConnection || effectiveSingleMode) return;

    const formValues = form.getValues();
    const nextConnectionName = getNextConnectionName();
    const draftConfigMapApi =
      integrationHasConfigMap && formValues.draftConfigMap
        ? mapConfigMapToApiFormat(
            formValues.draftConfigMap,
            integration.config_map,
          )
        : undefined;

    try {
      setIsLoading(true);
      const response = await promoteDraftToProductionConnection({
        updateConnection,
        draftConnection: {
          ...draftConnection,
          connection_name: nextConnectionName,
        },
        // Use the credentials the user just typed on the Production tab: these
        // are the plaintext values the backend confirmed match the draft. The
        // draftConfig form values are seeded from the existing connection and
        // mask sensitive fields, so reusing them could wipe the draft's secret.
        credentials: formValues.publishedConfig ?? {},
        integration,
        configMap: draftConfigMapApi,
      });

      const updated = response.book_connection_descriptor;
      if (
        updated?.status?.state ===
        V2BookConnectionState.BOOK_CONNECTION_STATE_FAILED
      ) {
        toast.error(
          tIntegrations('integrations.editConnection.validationFailed'),
        );
      } else if (showSuccessToast) {
        toast.success(
          hasInactiveConnection
            ? tIntegrations('integrations.editConnection.inactiveSaved')
            : tIntegrations('integrations.editConnection.success'),
        );
      }
      onSuccess(updated, updated);
      form.reset();
      resetReuseAlert();
    } catch (error) {
      captureException(error);
      toast.error(tIntegrations('integrations.editConnection.error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Check if we have at least one valid connection
  // A connection is valid for editing as long as a descriptor exists. The
  // presence of an authenticationId is NOT required — renaming works for
  // no-auth connections too, and credential fields are hidden when absent.
  const hasValidConnection = !!primaryConnection;

  const keyValueCredentialIds = getKeyValueCredentialIds(
    integration,
    authenticationId,
  );
  const hasInvalidCredentialKeys = hasInvalidKeyValueCredentials(
    form.watch(
      keyValueCredentialIds.flatMap(id => [
        `draftConfig.${id}`,
        `publishedConfig.${id}`,
      ]),
    ),
  );

  const { confirmEmptyValues, dialogProps: emptyCredentialValuesDialogProps } =
    useEmptyCredentialValuesConfirmation();

  const handleSave = async () => {
    try {
      if (!(await validateSave())) return;
    } catch (error) {
      captureException(error);
      toast.error(tIntegrations('integrations.editConnection.error'));
      return;
    }

    const submittedConfigs =
      syncCredentials || draftOnlyMode
        ? ['draftConfig']
        : publishOnlyMode
          ? ['publishedConfig']
          : ['draftConfig', 'publishedConfig'];
    // Saved key/value credentials are prefilled with their names and empty
    // values, so only credentials the user edited can hold intended blanks.
    const editedKeyValuePaths = submittedConfigs
      .flatMap(config => keyValueCredentialIds.map(id => `${config}.${id}`))
      .filter(path => form.getFieldState(path).isDirty);
    confirmEmptyValues(
      form.getValues(editedKeyValuePaths),
      () => void saveConnection(),
    );
  };

  const draftCredentialName = defaultCredentialDisplayName(
    integration.display_name || integration.name || '',
    connectionName ?? '',
  );

  const renderManagedCredential = (environment: EnvironmentType) =>
    connectionAuthentication && (
      <ManagedConnectionCredential
        integration={integration}
        authentication={connectionAuthentication}
        idPrefix={`edit-${environment.toLowerCase()}-credential`}
        currentCredentialId={currentCredentialIdFor(environment)}
        selectedCredentialId={chosenCredentialIds[environment]}
        onSelect={credentialId =>
          setChosenCredentialIds(prev => ({
            ...prev,
            [environment]: credentialId,
          }))
        }
        defaultDisplayName={
          environment === EnvironmentType.Published && !effectiveSingleMode
            ? tIntegrations(
                'integrations.addConnection.productionCredentialName',
                { name: draftCredentialName },
              )
            : draftCredentialName
        }
        sharedApp={sharedApp}
        ownApps={ownOAuthApps}
        onRedirect={(authorizeUrl, credentialId) =>
          redirectToConsent(environment, authorizeUrl, credentialId)
        }
        defaultOpen={resumeEnvironment === environment && !!resumeError}
        error={resumeEnvironment === environment ? resumeError : undefined}
        disabled={isLoading}
        readOnly={!canChangeCredential}
        usesInlineCredentials={hasInlineCredentials(environment)}
        onUnsavedEditChange={hasUnsaved =>
          setUnsavedCredentialEdits(prev =>
            !!prev[environment] === hasUnsaved
              ? prev
              : { ...prev, [environment]: hasUnsaved },
          )
        }
      />
    );

  const watchedPublishedConfig = form.watch('publishedConfig');
  const { showReuseAlert, setReuseAlertSnapshot, resetReuseAlert } =
    useReuseTestConnectionAlert(watchedPublishedConfig);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          // Definite height (mirrors AddConnectionDialog) so the nested flex
          // wrappers propagate a bounded height and the inner overflow-y-auto
          // scrolls. `max-h` alone leaves the nested flex-1 levels without a
          // definite height, so tall content overflowed instead of scrolling.
          'h-[calc(100vh-5rem)] lg:h-[calc(100vh-10rem)] [@media(max-height:800px)]:lg:h-[calc(100vh-5rem)] [@media(max-height:800px)]:lg:max-h-[calc(100vh-5rem)] flex flex-col overflow-hidden transition-[max-width] duration-300',
          openSidePanel ? 'sm:max-w-[1180px]' : 'sm:max-w-xl',
        )}
        onOpenAutoFocus={e => e.preventDefault()}
        aria-describedby={undefined}
      >
        {/* Raw flex div (not lattice <Flex>) for the two-pane row: it must be a
            plain flex container with default `align-items: stretch` so the
            body column stretches to full height and the inner overflow-y-auto
            scrolls. This mirrors AddConnectionDialog, which scrolls correctly. */}
        <div className="flex flex-1 min-h-0 sm:min-h-[280px] -mx-6 overflow-hidden relative">
          <Flex
            direction="column"
            className={cn(
              'flex-1 min-w-0 min-h-0 overflow-hidden px-6',
              openSidePanel && 'mr-[612px]',
              'transition-[margin] duration-300',
            )}
          >
            {/* pr-8 keeps the assistant button clear of the dialog's absolute close X. */}
            <Flex
              align="start"
              justify="between"
              gap={3}
              className="mb-4 w-full shrink-0 pr-8"
            >
              <DialogHeader className="min-w-0">
                <DialogTitle>
                  {tIntegrations('integrations.editConnection.title')}
                </DialogTitle>
                {diagnosedConnection && (
                  <Flex
                    align="center"
                    gap={2}
                    className="flex-wrap"
                    data-testid="edit-connection-header-meta"
                  >
                    {shouldShowHealthDetails(diagnosedConnection.status) ? (
                      <ConnectionDiagnosticsPopover
                        connection={diagnosedConnection}
                        displayName={
                          integration.display_name || integration.name
                        }
                        showAnalysis={false}
                      >
                        {diagnosedStatusTag}
                      </ConnectionDiagnosticsPopover>
                    ) : (
                      diagnosedStatusTag
                    )}
                  </Flex>
                )}
              </DialogHeader>
              {isAssistantEnabled &&
                primaryConnection?.connection_id &&
                !isAssistantOpen && (
                  <IntegrationAssistantBanner
                    compact
                    variant="diagnosis"
                    onOpenAssistant={() => setOpenSidePanel('assistant')}
                  />
                )}
            </Flex>

            {isLoadingIntegration ? (
              <Flex
                align="center"
                justify="center"
                className="min-h-[300px] w-full flex-1"
              >
                <Spinner size="xl" />
              </Flex>
            ) : !hasValidConnection ? (
              <Flex
                align="center"
                justify="center"
                className="min-h-[300px] w-full flex-1"
              >
                <Text color="muted">
                  {tIntegrations(
                    'integrations.editConnection.noConnectionsFound',
                  )}
                </Text>
              </Flex>
            ) : (
              <Form {...form}>
                <form
                  onSubmit={e => e.preventDefault()}
                  className="flex flex-col flex-1 min-h-0 relative"
                >
                  <div
                    className={cn(
                      'flex flex-col flex-1 min-h-0',
                      isWaitingForReady && 'opacity-25 pointer-events-none',
                    )}
                  >
                    {/* Hidden field to track active environment in form state */}
                    <FormField
                      control={form.control}
                      name="activeEnvironment"
                      render={({ field }) => (
                        <FormControl>
                          <input type="hidden" {...field} />
                        </FormControl>
                      )}
                    />
                    <div className="shrink-0 space-y-4">
                      <FormField
                        control={form.control}
                        name="connectionName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {tIntegrations(
                                'integrations.editConnection.connectionName',
                              )}
                            </FormLabel>
                            <FormControl>
                              <Input
                                data-testid="edit-connection-name-input"
                                className="w-full"
                                placeholder={tIntegrations(
                                  'integrations.addConnection.connectionName.placeholder',
                                )}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                            {publishOnlyMode && (
                              <Text level="small" color="muted">
                                {tIntegrations(
                                  'integrations.editConnection.publishOnlyMode',
                                )}
                              </Text>
                            )}
                            {draftOnlyMode && (
                              <Text level="small" color="muted">
                                {tIntegrations(
                                  'integrations.editConnection.draftOnlyMode',
                                )}
                              </Text>
                            )}
                          </FormItem>
                        )}
                      />
                      <Separator />
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 py-1">
                      <Flex direction="column" gap={4}>
                        {isOAuthAuthCode && !usesManagedCredentials ? (
                          <Flex
                            direction="column"
                            gap={2}
                            className="bg-muted rounded-xs p-4"
                          >
                            <Flex align="center" gap={2}>
                              <Icon type="Info" size="sm" />
                              <Text weight="medium">
                                {tIntegrations(
                                  'integrations.editConnection.oauthNotEditable.title',
                                )}
                              </Text>
                            </Flex>
                            <Text level="small" color="muted">
                              {tIntegrations(
                                'integrations.editConnection.oauthNotEditable.description',
                              )}
                            </Text>
                          </Flex>
                        ) : effectiveSingleMode ? (
                          // Single connection mode - show simple form without tabs
                          <Flex direction="column" gap={4} className="w-full">
                            <Text level="small" color="muted">
                              {tIntegrations(
                                'integrations.editConnection.singleConnectionMode',
                              )}
                            </Text>
                            {usesManagedCredentials ? (
                              renderManagedCredential(EnvironmentType.Draft)
                            ) : isOAuthType ? (
                              <OAuthClientCredentialsStep
                                integration={integration}
                                authMethodId={authenticationId}
                                control={
                                  form.control as unknown as Control<FieldValues>
                                }
                                fieldPrefix="draftConfig"
                              />
                            ) : (
                              <CustomCredentialsStep
                                integration={integration}
                                authMethodId={authenticationId}
                                control={
                                  form.control as unknown as Control<FieldValues>
                                }
                                fieldPrefix="draftConfig"
                                isEditMode
                              />
                            )}
                            {/* Config map fields for single connection mode */}
                            {integrationHasConfigMap && (
                              <ConfigMapStep
                                integration={integration}
                                control={form.control}
                                fieldPrefix="draftConfigMap"
                              />
                            )}
                          </Flex>
                        ) : (
                          <Tabs
                            value={activeEnvironment}
                            onValueChange={handleEnvironmentChange}
                            className="w-full"
                          >
                            <Flex direction="column" gap={4} className="w-full">
                              <Flex
                                align="center"
                                justify="between"
                                // Offsets the scroll container's py-1: -top-1/pt-1 close the see-through strip, -mt-1 keeps the resting and stuck positions equal.
                                className="sticky -top-1 z-10 -mt-1 w-full bg-background pt-1 pb-1"
                              >
                                <TabsList className="w-full">
                                  {draftConnection && (
                                    <TabsTrigger
                                      value={EnvironmentType.Draft}
                                      disabled={publishOnlyMode}
                                    >
                                      {tIntegrations(
                                        'integrations.connections.environment.Test',
                                      )}
                                    </TabsTrigger>
                                  )}
                                  {/* Show published tab if connection exists OR if draft exists (can create) */}
                                  {(publishedConnection || draftConnection) && (
                                    <TabsTrigger
                                      value={EnvironmentType.Published}
                                      disabled={draftOnlyMode}
                                    >
                                      {tIntegrations(
                                        'integrations.connections.environment.Production',
                                      )}
                                    </TabsTrigger>
                                  )}
                                </TabsList>
                              </Flex>
                              {isNoAuthFallback && (
                                <Flex align="center" gap={2}>
                                  <Checkbox
                                    checked={syncCredentials}
                                    onCheckedChange={checked =>
                                      form.setValue(
                                        'syncCredentials',
                                        !!checked,
                                        {
                                          shouldDirty: true,
                                        },
                                      )
                                    }
                                  />
                                  <Text level="small">
                                    {tIntegrations(
                                      'integrations.connections.syncCredentials.title',
                                    )}
                                  </Text>
                                </Flex>
                              )}
                              {draftConnection && (
                                <TabsContent
                                  data-testid="edit-connection-draft-panel"
                                  value={EnvironmentType.Draft}
                                  forceMount
                                  className={cn('w-full', {
                                    hidden:
                                      activeEnvironment !==
                                      EnvironmentType.Draft,
                                  })}
                                >
                                  {usesManagedCredentials ? (
                                    renderManagedCredential(
                                      EnvironmentType.Draft,
                                    )
                                  ) : isOAuthType ? (
                                    <OAuthClientCredentialsStep
                                      integration={integration}
                                      authMethodId={authenticationId}
                                      control={
                                        form.control as unknown as Control<FieldValues>
                                      }
                                      fieldPrefix="draftConfig"
                                    />
                                  ) : (
                                    <CustomCredentialsStep
                                      integration={integration}
                                      authMethodId={authenticationId}
                                      control={
                                        form.control as unknown as Control<FieldValues>
                                      }
                                      fieldPrefix="draftConfig"
                                      isEditMode
                                    />
                                  )}
                                  {/* Config map fields for draft tab */}
                                  {integrationHasConfigMap && (
                                    <ConfigMapStep
                                      integration={integration}
                                      control={form.control}
                                      fieldPrefix="draftConfigMap"
                                    />
                                  )}
                                </TabsContent>
                              )}
                              {(publishedConnection || draftConnection) && (
                                <TabsContent
                                  data-testid="edit-connection-published-panel"
                                  value={EnvironmentType.Published}
                                  forceMount
                                  className={cn('w-full', {
                                    hidden:
                                      activeEnvironment !==
                                      EnvironmentType.Published,
                                  })}
                                >
                                  {!publishedConnection && draftConnection && (
                                    <Flex
                                      direction="column"
                                      gap={2}
                                      className="bg-muted rounded-xs p-4 mb-4"
                                    >
                                      <Flex align="center" gap={2}>
                                        <Icon type="Info" size="sm" />
                                        <Text weight="medium" level="small">
                                          {tIntegrations(
                                            'integrations.editConnection.publishedWillBeCreated.title',
                                          )}
                                        </Text>
                                      </Flex>
                                      <Text level="small" color="muted">
                                        {tIntegrations(
                                          'integrations.editConnection.publishedWillBeCreated.description',
                                        )}
                                      </Text>
                                    </Flex>
                                  )}
                                  {usesManagedCredentials ? (
                                    renderManagedCredential(
                                      EnvironmentType.Published,
                                    )
                                  ) : isOAuthType ? (
                                    <OAuthClientCredentialsStep
                                      integration={integration}
                                      authMethodId={authenticationId}
                                      control={
                                        form.control as unknown as Control<FieldValues>
                                      }
                                      fieldPrefix="publishedConfig"
                                      disabled={syncCredentials}
                                    />
                                  ) : (
                                    <CustomCredentialsStep
                                      integration={integration}
                                      authMethodId={authenticationId}
                                      control={
                                        form.control as unknown as Control<FieldValues>
                                      }
                                      fieldPrefix="publishedConfig"
                                      disabled={syncCredentials}
                                      isEditMode
                                    />
                                  )}
                                  {showReuseAlert && (
                                    <ReuseTestConnectionAlert
                                      onReuse={handleReuseTestConnection}
                                      disabled={isLoading}
                                      className="my-6"
                                    />
                                  )}
                                  {/* Config map fields for published tab */}
                                  {integrationHasConfigMap && (
                                    <ConfigMapStep
                                      integration={integration}
                                      control={form.control}
                                      fieldPrefix="publishedConfigMap"
                                      disabled={syncCredentials}
                                    />
                                  )}
                                </TabsContent>
                              )}
                            </Flex>
                          </Tabs>
                        )}
                      </Flex>
                    </div>

                    <DialogFooter className="shrink-0 flex justify-between w-full flex-row sm:justify-between pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isLoading}
                      >
                        {tCommon('common.action.cancel')}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => void handleSave()}
                        disabled={
                          isLoading ||
                          (!isDirty && !hasCredentialChanges) ||
                          hasInvalidCredentialKeys ||
                          showReuseAlert
                        }
                        isLoading={isLoading}
                      >
                        {tCommon('common.action.save')}
                      </Button>
                    </DialogFooter>
                  </div>
                  {isWaitingForReady && !panelConnection && (
                    <Flex
                      direction="column"
                      gap={2}
                      align="center"
                      justify="center"
                      className="absolute inset-0"
                    >
                      <Flex gap={2} align="center" justify="center">
                        <Spinner />
                        <Text weight="medium" className="text-center">
                          {tIntegrations(
                            'integrations.addConnection.connectingTitle',
                            {
                              integrationName:
                                integration.display_name || integration.name,
                            },
                          )}
                        </Text>
                      </Flex>
                      <Text color="muted" level="small" className="text-center">
                        {tIntegrations(
                          isConnectionTakingTooLong
                            ? 'integrations.addConnection.connectingTooLong'
                            : 'integrations.addConnection.connectingSubtitle',
                        )}
                      </Text>
                    </Flex>
                  )}
                </form>
              </Form>
            )}
          </Flex>
          {isAssistantEnabled && primaryConnection?.connection_id && (
            <IntegrationAssistantPanel
              integrationName={integration.name || ''}
              integrationDisplayName={
                integration.display_name || integration.name || ''
              }
              integrationVersion={integration.version || ''}
              organizationId={organizationId}
              workspaceId={workspaceId}
              selectedAuthId={undefined}
              connectionId={primaryConnection.connection_id}
              diagnosis={completedDiagnosis}
              isDiagnosisLoading={diagnosisInsight.isLoading}
              disabled={isLoading || isWaitingForReady}
              isOpen={isAssistantOpen}
              onClose={() => setOpenSidePanel(null)}
            />
          )}
        </div>
        <EmptyCredentialValuesConfirmDialog
          {...emptyCredentialValuesDialogProps}
        />
      </DialogContent>
    </Dialog>
  );
}
